/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { useState, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { twMerge } from 'tailwind-merge';
import {
  BuildingOfficeIcon,
  TruckIcon,
  CubeIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useAuthHttp } from '@hooks';
import { nowLocalDate } from '@utils';
import { AVAILABLE_WAREHOUSES_URL, CUSTOMER_GET } from '@constants';
import { Customer } from '@views/CustomerMaster/customerRecord';
import { FormPage } from '../../tailwindComponents/FormPage';
import { Input } from '../../tailwindComponents/Input';
import { Select } from '../../tailwindComponents/Select';
import { Datepicker } from '../../tailwindComponents/Datepicker';
import { Timepicker } from '../../tailwindComponents/Timepicker';
import { Textarea } from '../../tailwindComponents/Textarea';
import { Subheader } from '../../tailwindComponents/Subheader';
import { ToggleSwitch } from '../../tailwindComponents/ToggleSwitch';
import { ShipmentMethodCard } from '../../tailwindComponents/ShipmentMethodCard';
import { Autocomplete } from '../../tailwindComponents/Autocomplete';
import {
  NewOutboundShipmentFormValues,
  defaultFormValues,
  ShipmentMethodOption,
  WarehouseOption,
  ProductItem,
} from './types';
import { schemas, stepFields } from './schema';
import { ProductsLoadStep } from './ProductsLoadStep';

const STEPS = ['Load', 'Shipment', 'Route', 'Confirmation'];

// Mock data for dropdowns
const vehicleOptions = [
  { label: 'Small van', value: 'small-van' },
  { label: 'Large van', value: 'large-van' },
  { label: 'Rigid truck', value: 'rigid-truck' },
  { label: 'Articulated', value: 'articulated' },
];

const curtainBoxOptions = [
  { label: 'Curtain', value: 'curtain' },
  { label: 'Box body', value: 'box-body' },
  { label: 'Either', value: 'either' },
];

const yesNoOptions = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

const unitOptions = [
  { label: 'm', value: 'm' },
  { label: 'cm', value: 'cm' },
  { label: 'ft', value: 'ft' },
];

const waitingTimeUnitOptions = [
  { label: 'minutes', value: 'minutes' },
  { label: 'hours', value: 'hours' },
];

// Diamond icon for Gainshare
function DiamondIcon() {
  return (
    <svg
      className='w-4 h-4 text-primary'
      viewBox='0 0 24 24'
      fill='currentColor'
    >
      <path d='M12 2L2 9l10 13 10-13L12 2zm0 3.84L18.26 9 12 18.54 5.74 9 12 5.84z' />
    </svg>
  );
}

// Skeleton card component for loading state
function SkeletonCard() {
  return (
    <div className='flex flex-col items-center p-4 rounded-xl border-2 border-gray-200 bg-white min-w-[160px]'>
      {/* Badge skeleton */}
      <div className='h-5 w-20 bg-gray-200 rounded animate-pulse mb-2' />
      {/* Price skeleton */}
      <div className='flex items-center gap-2 mb-3'>
        <div className='h-8 w-16 bg-gray-200 rounded animate-pulse' />
        <div className='h-4 w-12 bg-gray-200 rounded animate-pulse' />
      </div>
      {/* Button skeleton */}
      <div className='h-12 w-full rounded-lg bg-gray-200 animate-pulse' />
    </div>
  );
}

const shipmentMethods: ShipmentMethodOption[] = [
  {
    id: 'warehouse-lsp',
    title: 'Warehouse LSP',
    description: 'Existing logistics partner manages pickup',
    price: '~$1,870.54',
  },
  {
    id: 'pallet-network',
    title: 'Pallet Network',
    description: 'Shared freight lane for palletised goods',
    price: '~$970.00',
  },
  {
    id: 'courier-network',
    title: 'Courier Network',
    description: 'Parcel or express delivery',
    price: '~$2,347.00',
  },
  {
    id: 'spot-transport',
    title: 'Spot Transport Booking',
    description: 'Ad-hoc shipment (manual booking)',
    price: '~$1,430',
  },
  {
    id: 'optimised-collaborative',
    title: 'Optimised / Collaborative Shipment',
    description: 'Grouped loads across customers to reduce cost',
    price: '~$875.56',
    recommended: true,
  },
];

interface NewOutboundShipmentFormProps {
  onSubmit: (data: NewOutboundShipmentFormValues) => void;
  isSubmitting: boolean;
  header: string;
  pages: { name: string; href: string; current: boolean }[];
}

export function NewOutboundShipmentForm({
  onSubmit,
  isSubmitting,
  header,
  pages,
}: NewOutboundShipmentFormProps) {
  const authHttp = useAuthHttp();
  const [currentStep, setCurrentStep] = useState(1);
  const [warehouseOptions, setWarehouseOptions] = useState<WarehouseOption[]>(
    []
  );
  const [warehouseAddress, setWarehouseAddress] = useState<string>('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerAddress, setCustomerAddress] = useState<string>('');
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);

  // Step 3 (Route) state
  const [selectedQuote, setSelectedQuote] = useState('optimised');
  const [secondPriority, setSecondPriority] = useState<Record<string, boolean>>(
    {
      optimised: false,
      value: true,
      greenest: false,
      best: false,
      express: false,
      economy: false,
      premium: true,
      standard: false,
    }
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const methods = useForm<NewOutboundShipmentFormValues>({
    shouldUnregister: false,
    resolver: yupResolver(schemas[currentStep - 1]),
    mode: 'onChange',
    defaultValues: {
      ...defaultFormValues,
      desiredPickupDate: nowLocalDate(),
      desiredDeliveryDate: nowLocalDate(),
    },
  });

  const { formState, control, watch, setValue } = methods;

  // Watch for controlled values
  const shipmentMethod = watch('shipmentMethod');
  const selectedProducts = watch('selectedProducts');

  // Helper to find selected option for Select components
  const findOption = (
    options: { label: string; value: string }[],
    value: string
  ) => options.find((opt) => opt.value === value) || null;

  // Fetch warehouses and customers on mount
  useEffect(() => {
    async function fetchWarehouses() {
      setLoadingWarehouses(true);
      try {
        const {
          data: { matches },
        } = await authHttp.post(AVAILABLE_WAREHOUSES_URL, {
          pagination: {
            lastItemSort: null,
            itemsPerPage: 25,
          },
        });
        setWarehouseOptions(
          matches.map(
            (m: {
              warehouse: { warehouseId: string; name: string; address: string };
            }) => ({
              id: m.warehouse.warehouseId,
              name: m.warehouse.name,
              address: m.warehouse.address,
            })
          )
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        setLoadingWarehouses(false);
      }
    }

    async function fetchCustomers() {
      try {
        const { data } = await authHttp.post(CUSTOMER_GET, {
          pagination: {
            itemsPerPage: 100,
          },
        });
        if (data) setCustomers(data.customers);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }

    fetchWarehouses();
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle products change from Step 1
  const handleProductsChange = (products: ProductItem[]) => {
    setValue('selectedProducts', products);
  };

  // Step 1: Products Load
  const renderStep1 = () => (
    <ProductsLoadStep
      products={selectedProducts}
      onProductsChange={handleProductsChange}
    />
  );

  // Step 2: Shipment Details (previously Step 1)
  const renderStep2 = () => (
    <div className='grid grid-cols-2 gap-x-8'>
      {/* Left Column */}
      <div className='flex flex-col gap-y-6'>
        {/* Enter shipment's details */}
        <Subheader subHeader="Enter shipment's details" />
        <p className='text-sm text-gray-500 -mt-4'>
          Enter the details of your outbound shipment.
        </p>
        <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
          <div className='grid grid-cols-2 gap-4'>
            <Controller
              name='purchaseOrderNumber'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label='Purchase order number'
                  placeholder=''
                  sizeVariant='sm'
                  isInvalid={!!formState.errors.purchaseOrderNumber}
                  validationMessage={
                    formState.errors.purchaseOrderNumber?.message
                  }
                />
              )}
            />
            <Controller
              name='shipmentId'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label='Shipment ID'
                  placeholder=''
                  sizeVariant='sm'
                  isInvalid={!!formState.errors.shipmentId}
                  validationMessage={formState.errors.shipmentId?.message}
                />
              )}
            />
          </div>
        </div>

        {/* Origin */}
        <Subheader subHeader='Origin' />
        <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
          <Controller
            name='originWarehouse'
            control={control}
            render={({ field }) => (
              <div>
                <Autocomplete
                  label='Origin Despatch Warehouse'
                  options={warehouseOptions.map((w) => ({
                    id: w.id,
                    label: w.name,
                    address: w.address,
                  }))}
                  loading={loadingWarehouses}
                  value={field.value}
                  onInputChange={() => {}}
                  onChange={(option) => {
                    if (!option) return;
                    setValue('originWarehouseId', String(option.id));
                    field.onChange(option.label);
                    setWarehouseAddress(option.address || '');
                  }}
                  isInvalid={!!formState.errors.originWarehouse}
                  validationMessage={formState.errors.originWarehouse?.message}
                />
                {warehouseAddress && (
                  <p className='mt-1 text-xs text-gray-500'>
                    {warehouseAddress}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        {/* Destination */}
        <Subheader subHeader='Destination' />
        <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
          <Controller
            name='client'
            control={control}
            render={({ field }) => (
              <div>
                <Autocomplete
                  label='Client'
                  options={customers.map((c) => ({
                    id: String(c.id),
                    label: c.name,
                    address: c.address,
                  }))}
                  value={field.value}
                  onInputChange={(val) => field.onChange(val)}
                  onChange={(option) => {
                    if (!option) return;
                    setValue('clientId', String(option.id));
                    field.onChange(option.label);
                    setCustomerAddress(option.address || '');
                  }}
                  isInvalid={!!formState.errors.client}
                  validationMessage={formState.errors.client?.message}
                />
                {customerAddress && (
                  <p className='mt-1 text-xs text-gray-500'>
                    {customerAddress}
                  </p>
                )}
              </div>
            )}
          />

          <div>
            <label className='block text-sm font-medium text-gray-900 font-inter mb-2'>
              Desired Pick-up
            </label>
            <div className='flex items-center gap-2'>
              <Controller
                name='desiredPickupDate'
                control={control}
                render={({ field }) => (
                  <Datepicker
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    isInvalid={!!formState.errors.desiredPickupDate}
                    validationMessage={
                      formState.errors.desiredPickupDate?.message
                    }
                  />
                )}
              />
              <Controller
                name='desiredPickupTimeFrom'
                control={control}
                render={({ field }) => (
                  <Timepicker
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-900 font-inter mb-2'>
              Desired Delivery (RTA)
            </label>
            <div className='flex items-center gap-2'>
              <Controller
                name='desiredDeliveryDate'
                control={control}
                render={({ field }) => (
                  <Datepicker
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    isInvalid={!!formState.errors.desiredDeliveryDate}
                    validationMessage={
                      formState.errors.desiredDeliveryDate?.message
                    }
                  />
                )}
              />
              <Controller
                name='desiredDeliveryTimeFrom'
                control={control}
                render={({ field }) => (
                  <Timepicker
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>

          <Controller
            name='specialInstructions'
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                label='Special Instructions'
                placeholder='Type notes for the carrier or recipient...'
                optional
                maxLength={150}
              />
            )}
          />
        </div>

        {/* Drop-Off Site / Access Restrictions */}
        <Subheader subHeader='Drop-Off Site / Access Restrictions' />
        <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
          <Controller
            name='dropoffAccessRestrictions'
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                label='Access restrictions'
                placeholder='Type here...'
                isInvalid={!!formState.errors.dropoffAccessRestrictions}
                validationMessage={
                  formState.errors.dropoffAccessRestrictions?.message
                }
              />
            )}
          />

          <Controller
            name='dropoffSecurityInduction'
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                label='Security / ID / Induction'
                placeholder='Specify security requirements here...'
                isInvalid={!!formState.errors.dropoffSecurityInduction}
                validationMessage={
                  formState.errors.dropoffSecurityInduction?.message
                }
              />
            )}
          />

          <Controller
            name='dropoffPreBooking'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Pre-booking required / call on arrival'
                placeholder='e.g. "Call +49 176 12345678"'
                sizeVariant='sm'
                isInvalid={!!formState.errors.dropoffPreBooking}
                validationMessage={formState.errors.dropoffPreBooking?.message}
              />
            )}
          />

          <div className='grid grid-cols-2 gap-4'>
            <Controller
              name='dropoffWaitingTime'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label='Waiting time allowed'
                  placeholder=''
                  sizeVariant='sm'
                />
              )}
            />
            <Controller
              name='dropoffWaitingTimeUnit'
              control={control}
              render={({ field }) => (
                <Select
                  label='Unit'
                  options={waitingTimeUnitOptions}
                  selected={findOption(waitingTimeUnitOptions, field.value)}
                  setSelected={(opt) => field.onChange(opt.value)}
                />
              )}
            />
          </div>
        </div>

        {/* Vehicle and equipment requirements */}
        <Subheader subHeader='Vehicle and equipment requirements' />
        <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
          <div className='grid grid-cols-2 gap-4'>
            <Controller
              name='preferredVehicle'
              control={control}
              render={({ field }) => (
                <Select
                  label='Preferred vehicle'
                  options={vehicleOptions}
                  selected={findOption(vehicleOptions, field.value)}
                  setSelected={(opt) => field.onChange(opt.value)}
                  isInvalid={!!formState.errors.preferredVehicle}
                  validationMessage={formState.errors.preferredVehicle?.message}
                />
              )}
            />
            <Controller
              name='curtainSideOrBoxBody'
              control={control}
              render={({ field }) => (
                <Select
                  label='Curtain-side or box body preference'
                  options={curtainBoxOptions}
                  selected={findOption(curtainBoxOptions, field.value)}
                  setSelected={(opt) => field.onChange(opt.value)}
                  isInvalid={!!formState.errors.curtainSideOrBoxBody}
                  validationMessage={
                    formState.errors.curtainSideOrBoxBody?.message
                  }
                />
              )}
            />
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <Controller
              name='tailLiftRequired'
              control={control}
              render={({ field }) => (
                <Select
                  label='Tail-lift required'
                  options={yesNoOptions}
                  selected={findOption(yesNoOptions, field.value)}
                  setSelected={(opt) => field.onChange(opt.value)}
                  isInvalid={!!formState.errors.tailLiftRequired}
                  validationMessage={formState.errors.tailLiftRequired?.message}
                />
              )}
            />
            <Controller
              name='moffettRequired'
              control={control}
              render={({ field }) => (
                <Select
                  label='Moffett / forklift required'
                  options={yesNoOptions}
                  selected={findOption(yesNoOptions, field.value)}
                  setSelected={(opt) => field.onChange(opt.value)}
                  isInvalid={!!formState.errors.moffettRequired}
                  validationMessage={formState.errors.moffettRequired?.message}
                />
              )}
            />
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <Controller
              name='minimumInternalHeight'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label='Minimum internal height/length'
                  placeholder=''
                  sizeVariant='sm'
                />
              )}
            />
            <Controller
              name='minimumInternalHeightUnit'
              control={control}
              render={({ field }) => (
                <Select
                  label='Unit'
                  options={unitOptions}
                  selected={findOption(unitOptions, field.value)}
                  setSelected={(opt) => field.onChange(opt.value)}
                />
              )}
            />
          </div>
        </div>

        {/* Loading / Unloading on Site */}
        <Subheader subHeader='Loading / Unloading on Site' />
        <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
          <Controller
            name='forkliftAtCollection'
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                label='Forklift available at collection'
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name='forkliftAtDelivery'
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                label='Forklift available at delivery'
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name='dockLoading'
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                label='Dock loading'
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name='handballRequired'
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                label='Handball required'
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name='palletTruckNeeded'
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                label='Pallet truck needed'
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </div>

      {/* Right Column - Shipment Method Selection */}
      <div className='flex flex-col gap-y-6'>
        <Subheader subHeader='Choose how this load will be dispatched' />
        <p className='text-sm text-gray-500 -mt-4'>
          Your choice determines carrier, ETA, and cost in the next step.
        </p>
        <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
          <label className='block text-sm font-medium text-gray-900 font-inter'>
            Select shipment method
          </label>
          <div className='flex flex-col gap-y-3'>
            {shipmentMethods.map((method) => (
              <ShipmentMethodCard
                key={method.id}
                title={method.title}
                description={method.description}
                price={method.price}
                selected={shipmentMethod === method.id}
                recommended={method.recommended}
                onClick={() => setValue('shipmentMethod', method.id)}
                icon={
                  method.id === 'warehouse-lsp' ? (
                    <BuildingOfficeIcon className='h-8 w-8' />
                  ) : method.id === 'pallet-network' ? (
                    <CubeIcon className='h-8 w-8' />
                  ) : method.id === 'courier-network' ? (
                    <TruckIcon className='h-8 w-8' />
                  ) : method.id === 'spot-transport' ? (
                    <TruckIcon className='h-8 w-8' />
                  ) : (
                    <SparklesIcon className='h-8 w-8' />
                  )
                }
              />
            ))}
          </div>
          {formState.errors.shipmentMethod && (
            <p className='text-sm text-red-600'>
              {formState.errors.shipmentMethod.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => {
    // Loading state for Step 3
    const isLoadingQuotes = false; // This would come from API state

    if (isLoadingQuotes) {
      return (
        <div className='flex flex-col items-center py-8'>
          {/* Search Icon */}
          <div className='mb-6'>
            <svg
              className='w-16 h-16 text-primary'
              viewBox='0 0 64 64'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <circle
                cx='28'
                cy='28'
                r='12'
                stroke='currentColor'
                strokeWidth='2'
              />
              <path
                d='M37 37L45 45'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
              />
              <path
                d='M20 28H36'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
              />
              <path
                d='M28 20V36'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
              />
              <circle
                cx='50'
                cy='20'
                r='8'
                stroke='currentColor'
                strokeWidth='2'
              />
              <path
                d='M47 20H53'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
              />
              <path
                d='M50 17V23'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
              />
            </svg>
          </div>

          {/* Loading Text */}
          <h2 className='text-lg font-semibold text-gray-900 mb-2'>
            Searching quotes and building routes...
          </h2>
          <p className='text-sm text-gray-500 text-center max-w-md mb-8'>
            We are identifying the best carrier with an optimal route schedule.
            Kindly wait as we continuously gather carrier availability
            information.
          </p>

          {/* Skeleton Layout matching loaded design */}
          <div className='w-full'>
            {/* Header Cards Row */}
            <div className='grid grid-cols-[180px_1fr_1fr_1fr_1fr] gap-4 mb-2'>
              <div /> {/* Empty first column for row labels */}
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>

            {/* Data Rows */}
            <div className='grid grid-cols-[180px_1fr_1fr_1fr_1fr] gap-4 py-3 border-b border-gray-100'>
              <div className='h-4 w-14 bg-gray-200 rounded animate-pulse' />
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className='h-4 w-24 bg-gray-200 rounded animate-pulse mx-auto'
                />
              ))}
            </div>

            <div className='grid grid-cols-[180px_1fr_1fr_1fr_1fr] gap-4 py-3 border-b border-gray-100'>
              <div className='h-4 w-32 bg-gray-200 rounded animate-pulse' />
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className='h-4 w-24 bg-gray-200 rounded animate-pulse mx-auto'
                />
              ))}
            </div>

            <div className='grid grid-cols-[180px_1fr_1fr_1fr_1fr] gap-4 py-3 border-b border-gray-100'>
              <div className='h-4 w-40 bg-gray-200 rounded animate-pulse' />
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className='h-4 w-24 bg-gray-200 rounded animate-pulse mx-auto'
                />
              ))}
            </div>

            <div className='grid grid-cols-[180px_1fr_1fr_1fr_1fr] gap-4 py-3'>
              <div className='h-4 w-28 bg-gray-200 rounded animate-pulse' />
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className='h-6 w-16 bg-gray-200 rounded-full animate-pulse mx-auto'
                />
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Mock quote data - would come from API
    const quotes = [
      {
        id: 'optimised',
        badge: 'Optimised',
        badgeVariant: 'primary' as const,
        rtaCount: 1,
        price: '£280',
        gainshare: '£15',
        validHours: 5,
        pickUp: '10:00 - 10:45',
        dropOff1: '13:00 - 14:00',
        dropOff2: '14:00 - 15:00',
        dropOff2Warning: true,
        secondPriority: false,
      },
      {
        id: 'value',
        badge: 'Value Service',
        badgeVariant: 'gray' as const,
        rtaCount: null,
        price: '£350',
        gainshare: '£8',
        validHours: 2,
        pickUp: '12:30 - 13:30',
        dropOff1: '15:30 - 17:00',
        dropOff2: '16:30 - 18:00',
        dropOff2Warning: false,
        secondPriority: true,
      },
      {
        id: 'greenest',
        badge: 'Greenest',
        badgeVariant: 'gray' as const,
        rtaCount: null,
        price: '£412',
        gainshare: '£5',
        validHours: 5,
        pickUp: '09:00 - 11:00',
        dropOff1: '14:00 - TBC',
        dropOff2: 'TBC - 18:00',
        dropOff2Warning: false,
        secondPriority: false,
      },
      {
        id: 'best',
        badge: 'Best Service',
        badgeVariant: 'gray' as const,
        rtaCount: null,
        price: '£505.25',
        gainshare: null,
        validHours: 12,
        pickUp: '08:30 - 09:30',
        dropOff1: '11:30 - 12:30',
        dropOff2: '14:30 - 15:30',
        dropOff2Warning: false,
        secondPriority: false,
      },
      {
        id: 'express',
        badge: 'Express',
        badgeVariant: 'gray' as const,
        rtaCount: 2,
        price: '£620',
        gainshare: '£22',
        validHours: 3,
        pickUp: '07:00 - 07:30',
        dropOff1: '09:30 - 10:00',
        dropOff2: '11:00 - 11:30',
        dropOff2Warning: false,
        secondPriority: false,
      },
      {
        id: 'economy',
        badge: 'Economy',
        badgeVariant: 'gray' as const,
        rtaCount: null,
        price: '£195',
        gainshare: '£3',
        validHours: 8,
        pickUp: '14:00 - 16:00',
        dropOff1: '18:00 - 20:00',
        dropOff2: '20:00 - 22:00',
        dropOff2Warning: true,
        secondPriority: false,
      },
      {
        id: 'premium',
        badge: 'Premium',
        badgeVariant: 'gray' as const,
        rtaCount: 3,
        price: '£750',
        gainshare: '£30',
        validHours: 4,
        pickUp: '06:00 - 06:30',
        dropOff1: '08:00 - 08:30',
        dropOff2: '09:30 - 10:00',
        dropOff2Warning: false,
        secondPriority: true,
      },
      {
        id: 'standard',
        badge: 'Standard',
        badgeVariant: 'gray' as const,
        rtaCount: null,
        price: '£320',
        gainshare: '£10',
        validHours: 6,
        pickUp: '11:00 - 12:00',
        dropOff1: '14:00 - 15:00',
        dropOff2: '16:00 - 17:00',
        dropOff2Warning: false,
        secondPriority: false,
      },
    ];

    const scrollLeft = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
      }
    };

    const scrollRight = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
      }
    };

    return (
      <div className='flex flex-col gap-6'>
        {/* Header */}
        <div className='flex items-center justify-between'>
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>
              Best options for your route
            </h2>
            <p className='text-sm text-gray-500'>
              Choose the best carrier with an optimal route schedule and price.
            </p>
          </div>
          <div className='flex gap-2'>
            <button
              type='button'
              onClick={scrollLeft}
              className='p-2 rounded-full border border-gray-200 hover:bg-gray-50'
            >
              <svg
                className='w-4 h-4 text-gray-500'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M15 19l-7-7 7-7'
                />
              </svg>
            </button>
            <button
              type='button'
              onClick={scrollRight}
              className='p-2 rounded-full border border-gray-200 hover:bg-gray-50'
            >
              <svg
                className='w-4 h-4 text-gray-500'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 5l7 7-7 7'
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Quote Cards and Data Grid */}
        <div ref={scrollContainerRef} className='w-full overflow-x-auto'>
          {/* Header Cards Row */}
          <div
            className='grid gap-3 mb-2'
            style={{
              gridTemplateColumns: `180px repeat(${quotes.length}, 200px)`,
            }}
          >
            <div className='sticky left-0 bg-gray-50 z-10' />{' '}
            {/* Empty first column for row labels */}
            {quotes.map((quote) => {
              const isSelected = selectedQuote === quote.id;
              return (
                <button
                  type='button'
                  key={quote.id}
                  className={twMerge(
                    'flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer transition-all',
                    isSelected
                      ? 'border-primary bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  )}
                  onClick={() => setSelectedQuote(quote.id)}
                >
                  {/* Badge */}
                  <div className='flex items-center gap-2 mb-2'>
                    <span
                      className={twMerge(
                        'text-base font-semibold',
                        isSelected ? 'text-primary' : 'text-gray-900'
                      )}
                    >
                      {quote.badge}
                    </span>
                    {quote.rtaCount && (
                      <span className='text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-primary'>
                        RTA +{quote.rtaCount}
                      </span>
                    )}
                  </div>
                  {/* Price */}
                  <div className='flex items-center gap-2 mb-3'>
                    <span className='text-3xl font-bold text-gray-900'>
                      {quote.price}
                    </span>
                    {quote.gainshare && (
                      <div className='flex items-center gap-1'>
                        <DiamondIcon />
                        <div className='flex flex-col'>
                          <span className='text-sm font-semibold text-green-600'>
                            {quote.gainshare}
                          </span>
                          <span className='text-xs text-gray-500'>
                            Gainshare
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Select Indicator */}
                  <div
                    className={twMerge(
                      'w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors flex flex-col items-center',
                      isSelected
                        ? 'bg-primary text-white'
                        : 'bg-white border border-gray-300 text-gray-700'
                    )}
                  >
                    <span>{isSelected ? 'Selected' : 'Select'}</span>
                    <span
                      className={twMerge(
                        'text-xs',
                        isSelected ? 'text-white/80' : 'text-gray-500'
                      )}
                    >
                      Valid for the next {quote.validHours} hours
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Data Rows */}
          <div
            className='grid gap-3 py-3 border-b border-gray-100'
            style={{
              gridTemplateColumns: `180px repeat(${quotes.length}, 200px)`,
            }}
          >
            <div className='text-sm text-gray-500 font-medium sticky left-0 bg-gray-50 z-10'>
              Pick-Up
            </div>
            {quotes.map((quote) => (
              <div key={quote.id} className='text-sm text-gray-900 text-center'>
                {quote.pickUp}
              </div>
            ))}
          </div>

          <div
            className='grid gap-3 py-3 border-b border-gray-100'
            style={{
              gridTemplateColumns: `180px repeat(${quotes.length}, 200px)`,
            }}
          >
            <div className='text-sm text-gray-500 font-medium sticky left-0 bg-gray-50 z-10'>
              Drop-Off 1: Tesco North
            </div>
            {quotes.map((quote) => (
              <div key={quote.id} className='text-sm text-gray-900 text-center'>
                {quote.dropOff1}
              </div>
            ))}
          </div>

          <div
            className='grid gap-3 py-3 border-b border-gray-100'
            style={{
              gridTemplateColumns: `180px repeat(${quotes.length}, 200px)`,
            }}
          >
            <div className='text-sm text-gray-500 font-medium sticky left-0 bg-gray-50 z-10'>
              Drop-Off 2: Sainsbury DC12
            </div>
            {quotes.map((quote) => (
              <div
                key={quote.id}
                className='flex items-center justify-center gap-1 text-sm text-gray-900'
              >
                {quote.dropOff2}
                {quote.dropOff2Warning && (
                  <span className='text-amber-500'>⚠</span>
                )}
              </div>
            ))}
          </div>

          <div
            className='grid gap-3 py-3'
            style={{
              gridTemplateColumns: `180px repeat(${quotes.length}, 200px)`,
            }}
          >
            <div className='text-sm text-gray-500 font-medium sticky left-0 bg-gray-50 z-10'>
              Second Priority
            </div>
            {quotes.map((quote) => (
              <div key={quote.id} className='flex items-center justify-center'>
                <ToggleSwitch
                  label=''
                  checked={secondPriority[quote.id]}
                  onChange={(checked) =>
                    setSecondPriority((prev) => ({
                      ...prev,
                      [quote.id]: checked,
                    }))
                  }
                />
              </div>
            ))}
          </div>
        </div>

        {/* Need other options section */}
        <div className='bg-white rounded-lg border border-gray-200 p-6 flex items-center justify-between'>
          <div>
            <h3 className='text-base font-semibold text-gray-900'>
              Need other options?
            </h3>
            <p className='text-sm text-gray-500'>
              Alternatively, you may request a quote for dedicated transport.
            </p>
          </div>
          <button
            type='button'
            className='flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50'
          >
            <svg
              className='w-4 h-4'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
              />
            </svg>
            Request Delivery Quote
          </button>
        </div>
      </div>
    );
  };

  const renderStep4 = () => {
    // Mock data for SKUs
    const skuItems = [
      {
        id: '1',
        name: 'Ananas',
        image: '/images/products/ananas.png',
        unitWeight: '2.5 kg',
        totalWeight: '125 kg',
        palletType: 'EU Pallet',
      },
      {
        id: '2',
        name: 'Coca Cola Classic',
        image: '/images/products/coca-cola.png',
        unitWeight: '1.29 kg',
        totalWeight: '90 kg',
        palletType: 'GB Pallet',
      },
      {
        id: '3',
        name: 'Pack of almonds',
        image: '/images/products/almonds.png',
        unitWeight: '0.8 kg',
        totalWeight: '34 kg',
        palletType: 'EU Half Pallet',
      },
    ];

    // Mock data for pallet summary
    const palletTypes = [
      {
        type: 'EU Pallet',
        dimensions: '1200×800 mm',
        quantity: 2,
        weight: '1,820 kg',
        volume: '3.8 m³',
      },
      {
        type: 'EU Half Pallet',
        dimensions: '600×800 mm',
        quantity: 1,
        weight: '210 kg',
        volume: '0.5 m³',
      },
      {
        type: 'GB Pallet',
        dimensions: '1200×1000 mm',
        quantity: 2,
        weight: '710 kg',
        volume: '2.1 m³',
      },
    ];

    // Mock data for route
    const routePoints = [
      {
        type: 'warehouse',
        point: 'Warehouse A, 1235684',
        shipment: '',
        window: '10:00 - 10:45',
      },
      {
        type: 'destination',
        point: 'Tesco North, 155836',
        shipment: '#5478ff-SN',
        window: '13:00 - 14:00',
      },
    ];

    // Mock unavailable product
    const unavailableProduct = {
      name: 'Coca Cola Classic',
      skuId: 'fnc456ki',
      image: '/images/products/coca-cola.png',
    };

    return (
      <div className='grid grid-cols-[1fr_320px] gap-6'>
        {/* Left Column */}
        <div className='flex flex-col gap-y-6'>
          {/* Shipment details */}
          <div className='bg-white rounded-lg border border-gray-200 p-5'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-base font-semibold text-gray-900'>
                Shipment details
              </h3>
              <button
                type='button'
                onClick={() => setCurrentStep(2)}
                className='text-sm text-primary font-medium hover:underline'
              >
                Edit
              </button>
            </div>

            <div className='flex flex-col gap-y-2'>
              <div className='flex'>
                <span className='text-sm text-gray-500 w-40'>
                  Purchase order #:
                </span>
                <span className='text-sm text-gray-900'>453</span>
              </div>
              <div className='flex'>
                <span className='text-sm text-gray-500 w-40'>Shipment ID:</span>
                <span className='text-sm text-gray-900'>4345653</span>
              </div>
              <div className='flex flex-col gap-y-1 mt-2'>
                <div className='flex items-start'>
                  <span className='text-sm text-gray-500 w-40'>Warehouse:</span>
                  <span className='text-sm text-gray-900'>
                    Warehouse A, 1235684
                  </span>
                </div>
                <div className='flex items-start'>
                  <span className='text-sm text-gray-500 w-40' />
                  <span className='text-sm text-gray-500 flex items-center gap-1'>
                    <svg
                      className='w-4 h-4 text-gray-400 flex-shrink-0'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                      />
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                      />
                    </svg>
                    ABC Logistics, 12 High Street, Manchester, M1 2AB
                  </span>
                </div>
              </div>
              <div className='flex flex-col gap-y-1 mt-2'>
                <div className='flex items-start'>
                  <span className='text-sm text-gray-500 w-40'>Client:</span>
                  <span className='text-sm text-gray-900'>
                    Judith Belington
                  </span>
                </div>
                <div className='flex items-start'>
                  <span className='text-sm text-gray-500 w-40' />
                  <span className='text-sm text-gray-500 flex items-center gap-1'>
                    <svg
                      className='w-4 h-4 text-gray-400 flex-shrink-0'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                      />
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                      />
                    </svg>
                    Food Retailer, 34 Market Road, London, EC1M 6BY
                  </span>
                </div>
              </div>
              <div className='flex mt-2'>
                <span className='text-sm text-gray-500 w-40'>RTA:</span>
                <span className='text-sm text-gray-900'>15/06/2025</span>
              </div>
              <div className='flex mt-2'>
                <span className='text-sm text-gray-500 w-40'>
                  Special instructions:
                </span>
                <span className='text-sm text-gray-900'>
                  Deliver fast – fresh items.
                </span>
              </div>
            </div>
          </div>

          {/* SKUs */}
          <div className='bg-white rounded-lg border border-gray-200 p-5'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-base font-semibold text-gray-900'>SKUs</h3>
              <button
                type='button'
                onClick={() => setCurrentStep(1)}
                className='text-sm text-primary font-medium hover:underline'
              >
                Edit
              </button>
            </div>

            {/* SKU items */}
            <div className='flex flex-col gap-y-4'>
              {skuItems.map((sku) => (
                <div key={sku.id} className='flex items-center gap-4'>
                  {/* SKU Image */}
                  <div className='h-14 w-14 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0'>
                    {sku.image ? (
                      <img
                        src={sku.image}
                        alt={sku.name}
                        className='h-full w-full object-cover'
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <span className='text-xs text-gray-400'>IMG</span>
                    )}
                  </div>

                  {/* SKU Name */}
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-medium text-gray-900'>
                      {sku.name}
                    </p>
                  </div>

                  {/* Unit weight */}
                  <div className='text-right'>
                    <p className='text-xs text-gray-500'>Unit weight</p>
                    <p className='text-sm font-medium text-gray-900'>
                      {sku.unitWeight}
                    </p>
                  </div>

                  {/* Total weight */}
                  <div className='text-right'>
                    <p className='text-xs text-gray-500'>Total weight:</p>
                    <p className='text-sm font-medium text-gray-900'>
                      {sku.totalWeight}
                    </p>
                  </div>

                  {/* Pallet type */}
                  <div className='text-right'>
                    <p className='text-xs text-gray-500'>Pallet type</p>
                    <p className='text-sm font-medium text-gray-900'>
                      {sku.palletType}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Route */}
          <div className='bg-white rounded-lg border border-gray-200 p-5'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Route
            </h3>

            <div className='flex gap-x-6 mb-4'>
              <div className='flex items-center gap-2'>
                <span className='text-sm text-gray-500'>Route ID:</span>
                <span className='text-sm text-gray-900'>1398-id</span>
                <button
                  type='button'
                  className='text-gray-400 hover:text-gray-600'
                >
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'
                    />
                  </svg>
                </button>
              </div>
              <div className='flex items-center gap-2'>
                <span className='text-sm text-gray-500'>Date:</span>
                <span className='text-sm text-gray-900'>03/01/2025</span>
              </div>
            </div>

            {/* Route table header */}
            <div className='grid grid-cols-[auto_1fr_120px_120px] gap-4 py-2 border-b border-gray-200'>
              <span className='text-sm text-gray-500' />
              <span className='text-sm text-gray-500'>Point</span>
              <span className='text-sm text-gray-500'>Shipment</span>
              <span className='text-sm text-gray-500'>Window</span>
            </div>

            {/* Route points */}
            {routePoints.map((point, index) => (
              <div
                key={point.point}
                className='grid grid-cols-[auto_1fr_120px_120px] gap-4 py-3 border-b border-gray-50 items-center'
              >
                <div className='flex items-center'>
                  {point.type === 'warehouse' ? (
                    <svg
                      className='w-5 h-5 text-primary'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z'
                      />
                    </svg>
                  ) : (
                    <svg
                      className='w-5 h-5 text-primary'
                      fill='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z' />
                    </svg>
                  )}
                </div>
                <span className='text-sm text-gray-900'>{point.point}</span>
                <span className='text-sm text-gray-900'>{point.shipment}</span>
                <span className='text-sm text-gray-900'>{point.window}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column - Load Summary */}
        <div className='flex flex-col gap-4'>
          <div className='bg-white rounded-lg border border-gray-200 p-4'>
            <h3 className='text-base font-semibold text-gray-900 mb-1'>
              Load Summary
            </h3>
            <p className='text-xs text-gray-500 mb-4'>
              Totals automatically recalculate.
            </p>

            {/* Summary Stats */}
            <div className='space-y-3 mb-4'>
              <div className='flex justify-between text-sm'>
                <span className='text-gray-500'>Total SKUs</span>
                <span className='text-gray-900 font-medium'>6</span>
              </div>
              <div className='flex justify-between text-sm'>
                <span className='text-gray-500'>Total units</span>
                <span className='text-gray-900 font-medium'>10</span>
              </div>
              <div className='flex justify-between text-sm border-b border-gray-100 pb-3'>
                <span className='text-gray-500'>Total pallets</span>
                <span className='text-gray-900 font-medium'>9 pallets</span>
              </div>
            </div>

            {/* Pallet Breakdown */}
            {palletTypes.map((pallet) => (
              <div key={pallet.type} className='border-b border-gray-100 py-3'>
                <div className='flex justify-between items-start mb-2'>
                  <span className='text-sm font-medium text-gray-900'>
                    {pallet.type}
                  </span>
                  <span className='text-xs text-gray-500'>
                    {pallet.dimensions}
                  </span>
                </div>
                <div className='grid grid-cols-3 gap-2 text-xs'>
                  <div>
                    <p className='text-gray-500'>Quantity</p>
                    <p className='text-gray-900 font-medium'>
                      {pallet.quantity}
                    </p>
                  </div>
                  <div>
                    <p className='text-gray-500'>Weight</p>
                    <p className='text-gray-900 font-medium'>{pallet.weight}</p>
                  </div>
                  <div>
                    <p className='text-gray-500'>Volume</p>
                    <p className='text-gray-900 font-medium'>{pallet.volume}</p>
                  </div>
                </div>
              </div>
            ))}

            {/* Total Weight */}
            <div className='pt-3 flex justify-between text-sm'>
              <span className='text-gray-500'>Total weight</span>
              <span className='text-gray-900 font-semibold'>2,740 kg</span>
            </div>
          </div>

          {/* Unavailable SKUs Warning */}
          <div className='bg-red-50 rounded-lg border border-red-200 p-4'>
            <div className='flex items-center gap-2 mb-3'>
              <svg
                className='h-5 w-5 text-red-500'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
              <span className='text-sm font-medium text-red-700'>
                Currently unavailable product(s):
              </span>
            </div>

            <div className='flex items-center gap-3 bg-white rounded-lg p-2'>
              <div className='h-10 w-10 bg-gray-100 rounded flex items-center justify-center overflow-hidden'>
                {unavailableProduct.image ? (
                  <img
                    src={unavailableProduct.image}
                    alt={unavailableProduct.name}
                    className='h-full w-full object-cover'
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <span className='text-xs text-gray-400'>IMG</span>
                )}
              </div>
              <div>
                <p className='text-sm font-medium text-gray-900'>
                  {unavailableProduct.name}
                </p>
                <p className='text-xs text-gray-500'>
                  # {unavailableProduct.skuId}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      default:
        return renderStep1();
    }
  };

  return (
    <FormPage<NewOutboundShipmentFormValues>
      header={header}
      pages={pages}
      methods={methods}
      steps={STEPS}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
      stepFields={stepFields}
      submitText='Confirm Shipment'
    >
      {renderCurrentStep()}
    </FormPage>
  );
}
